import { Grid, Box, IconButton } from "@mui/material";
import {
  DataGrid,
  gridPreferencePanelStateSelector,
  GridPreferencePanelsValue,
  useGridApiRef,
  GridToolbarColumnsButton,
} from "@mui/x-data-grid";
import * as React from "react";
import ReactTableStyles from "@Components/ReactTable/ReactTableStyles.js";
import { CustomColumnToolbar } from "../ReactTable/ReactTable";
import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation } from "react-router-dom";

export default function ServerPaginatedTable({
  columns,
  checkboxSelection,
  disableRowSelectionOnClick,
  exportedFileName,
  disableColumnMenu,
  height = "70vh",
  id,
  getRowClassName,
  className,
  initialColumnVisibilityModel,
  rowCount,
  rows,
  isLoading,
  fetchPage,
  exportUrl,
  exportOnClick,
  sortBy,
  enableHorizontalScroll = false,
  enableExport = true,
}) {
  const location = useLocation();
  const cachePrefix = location.pathname.replace(/\//g, "_");
  const getCacheKey = (key) => `${cachePrefix}_${key}`;

  const column = true;
  const filters = true;
  const density = true;
  const exports = enableExport;
  const search = false;
  const sizeOptions = [5, 10, 20, 50];

  const [paginationModel, setPaginationModel] = useState(() => {
    const cached = sessionStorage.getItem(getCacheKey("queryOptions"));
    if (cached) {
      const parsed = JSON.parse(cached);
      return {
        page: parsed.page || 0,
        pageSize: parsed.size || 10,
      };
    }
    return {
      page: 0,
      pageSize: 10,
    };
  });

  const [sortModel, setSortModel] = useState(() => {
    const cached = sessionStorage.getItem(getCacheKey("queryOptions"));
    if (cached) {
      const parsed = JSON.parse(cached);
      return parsed.sortBy
        ? [{ field: parsed.sortBy, sort: parsed.sortDir }]
        : [];
    }
    return [];
  });
  const [filterModel, setFilterModel] = useState(() => {
    const savedFilters = sessionStorage.getItem(getCacheKey("filterState"));
    if (savedFilters && savedFilters !== "[]" && savedFilters !== "{}") {
      try {
        return JSON.parse(savedFilters);
      } catch (e) {
        return { items: [] };
      }
    }
    return { items: [] };
  });
  const [savedFilterModel, setSavedFilterModel] = useState(() => {
    const savedFilters = sessionStorage.getItem(getCacheKey("filterState"));
    if (savedFilters && savedFilters !== "[]" && savedFilters !== "{}") {
      try {
        return JSON.parse(savedFilters);
      } catch (e) {
        return { items: [] };
      }
    }
    return { items: [] };
  });

  const queryOptions = {
    page: paginationModel.page,
    size: paginationModel.pageSize,
    sortBy: sortModel.length > 0 ? sortModel[0].field : sortBy,
    sortDir: sortModel.length > 0 ? sortModel[0].sort : "desc",
  };

  // Cache query options when they change
  useEffect(() => {
    sessionStorage.setItem(
      getCacheKey("queryOptions"),
      JSON.stringify(queryOptions),
    );
  }, [paginationModel, sortModel]);

  const [isRestored, setIsRestored] = useState(false);

  useEffect(() => {
    const savedFilterState = sessionStorage.getItem(getCacheKey("filterState"));
    if (
      savedFilterState &&
      savedFilterState !== "[]" &&
      savedFilterState !== "{}"
    ) {
      try {
        const parsed = JSON.parse(savedFilterState);
        setFilterModel(parsed);
        setSavedFilterModel(parsed);

        // Load filtered data once
        fetchPage(queryOptions, parsed);
      } catch (e) {
        console.error("Error parsing saved filter state:", e);
        fetchPage(queryOptions, { items: [] });
      }
    } else {
      // no saved filters, load unfiltered data
      fetchPage(queryOptions, { items: [] });
    }
    setIsRestored(true);
  }, []);

  useEffect(() => {
    if (isRestored) {
      fetchPage(queryOptions, savedFilterModel);
    }
  }, [paginationModel, sortModel, isRestored]);

  const apiRef = useGridApiRef();
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  // Track filter panel open/close
  useEffect(() => {
    const unsubscribe = apiRef.current.subscribeEvent("stateChange", () => {
      const preferencePanelState = gridPreferencePanelStateSelector(
        apiRef.current.state,
      );
      setIsFilterPanelOpen(
        preferencePanelState.open &&
          preferencePanelState.openedPanelValue ===
            GridPreferencePanelsValue.filters,
      );
    });
    return () => {
      unsubscribe();
    };
  }, [apiRef]);

  // Apply Filters
  const applyFilters = () => {
    let newFilters = filterModel.items || [];
    let savedFilters = savedFilterModel.items || [];

    const mergedFilters = [...savedFilters];

    newFilters.forEach((newFilter) => {
      if (!newFilter.field || !newFilter.operator) return;

      const existingIndex = mergedFilters.findIndex(
        (f) => f.field === newFilter.field && f.operator === newFilter.operator,
      );

      if (existingIndex !== -1) {
        mergedFilters[existingIndex] = newFilter;
      } else {
        mergedFilters.push(newFilter);
      }
    });

    const finalFilterModel = { items: mergedFilters };

    sessionStorage.setItem(
      getCacheKey("filterState"),
      JSON.stringify(finalFilterModel),
    );
    setSavedFilterModel(finalFilterModel);
    setFilterModel(finalFilterModel);
    fetchPage(queryOptions, finalFilterModel);

    if (apiRef.current?.hideFilterPanel) {
      apiRef.current.hideFilterPanel();
    }
  };

  // Remove single filter
  const removeFilter = (index) => {
    const updatedFilters = savedFilterModel.items.filter((_, i) => i !== index);
    const finalFilterModel = { items: updatedFilters };
    sessionStorage.setItem(
      getCacheKey("filterState"),
      JSON.stringify(finalFilterModel),
    );
    setSavedFilterModel(finalFilterModel);
    setFilterModel(finalFilterModel);
    fetchPage(queryOptions, finalFilterModel);
  };

  // Enter key + Search button listener
  useEffect(() => {
    const handleClickOutside = (event) => {
      const path = event.composedPath();
      const isInsidePanel = path.some((el) =>
        el.classList?.contains("MuiDataGrid-panel"),
      );

      if (!isInsidePanel) {
        const isSearchButton = event.target.closest(
          '[data-is-search-button="true"]',
        );
        if (isSearchButton) {
          applyFilters();
        }
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        applyFilters();
      }
    };

    if (isFilterPanelOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFilterPanelOpen, queryOptions, filterModel]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    const saved = sessionStorage.getItem(getCacheKey("colVisibility"));
    return {
      ...initialColumnVisibilityModel,
      ...(saved ? JSON.parse(saved) : {}),
    };
  });

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    sessionStorage.setItem(
      getCacheKey("colVisibility"),
      JSON.stringify(newModel),
    );
  };
  return (
    <ReactTableStyles
      tableHeight={height}
      enableHorizontalScroll={enableHorizontalScroll}
    >
      <Grid container justifyContent="center" alignItems="center">
        <Grid item xs={12}>
          <DataGrid
            apiRef={apiRef}
            sx={{
              "& .MuiPaper-root.MuiDataGrid-paper": {
                transform: "rotate(45deg)!important",
              },
            }}
            getRowId={id && ((row) => row[id])}
            className={`${className ? className : ""} tableHeight `}
            getRowClassName={(params) => {
              let className =
                params.indexRelativeToCurrentPage % 2 === 0
                  ? "Mui-even"
                  : "Mui-odd";
              className += getRowClassName?.(params) ?? "";
              return className;
            }}
            classes={{
              root: "overrideRoot",
              cell: "overrideCell",
              columnHeader: "columnHeaderOverride",
              row: "overrideRow",
              virtualScroller: "overrideVirtualScroller",
            }}
            columns={columns}
            disableColumnMenu={disableColumnMenu}
            rows={rows}
            loading={isLoading}
            slots={{
              toolbar: () => (
                <>
                  {/* Active Filters */}
                  {savedFilterModel.items &&
                    savedFilterModel.items.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        {savedFilterModel.items.map((item, idx) =>
                          item.field && item.operator ? (
                            <>
                              <Box
                                key={idx}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  mr: 2,
                                  p: 1,
                                  background: "#f5f5f5",
                                  borderRadius: 1,
                                  fontSize: "0.95em",
                                  color: "#333",
                                  border: "1px solid #e0e0e0",
                                  mt: 1,
                                }}
                              >
                                <strong>
                                  {columns.find(
                                    (col) => col.field === item.field,
                                  )?.headerName || item.field}
                                </strong>{" "}
                                <span
                                  style={{ color: "#1976d2", margin: "0 4px" }}
                                >
                                  {item.operator}
                                </span>
                                {item.value !== undefined &&
                                  item.value !== "" && (
                                    <span>{String(item.value)}</span>
                                  )}
                                <IconButton
                                  size="small"
                                  onClick={() => removeFilter(idx)}
                                  sx={{ ml: 1 }}
                                >
                                  <CloseIcon fontSize="small" color="red" />
                                </IconButton>
                              </Box>
                              {idx < savedFilterModel.items.length - 1 && (
                                <span
                                  key={idx}
                                  style={{
                                    marginRight: "8px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  AND
                                </span>
                              )}
                            </>
                          ) : null,
                        )}
                      </Box>
                    )}
                  {CustomColumnToolbar(
                    column,
                    filters,
                    density,
                    exports,
                    exportedFileName,
                    search,
                    isFilterPanelOpen,
                    fetchPage,
                    exportUrl,
                    exportOnClick,
                  )}
                </>
              ),
            }}
            columnVisibilityModel={columnVisibilityModel} // controlled state
            onColumnVisibilityModelChange={handleColumnVisibilityChange} // sync to session
            initialState={{
              columns: {
                columnVisibilityModel: columnVisibilityModel,
              },
            }}
            checkboxSelection={checkboxSelection}
            disableRowSelectionOnClick={disableRowSelectionOnClick}
            pageSizeOptions={sizeOptions}
            paginationModel={paginationModel}
            sortModel={sortModel}
            filterModel={filterModel}
            paginationMode="server"
            sortingMode="server"
            filterMode="server"
            onPaginationModelChange={setPaginationModel}
            onSortModelChange={setSortModel}
            onFilterModelChange={(newModel) => {
              setFilterModel(newModel);
              if (!newModel.items || newModel.items.length === 0) {
                sessionStorage.setItem(
                  getCacheKey("filterState"),
                  JSON.stringify([]),
                );
                setSavedFilterModel({ items: [] });
                fetchPage(queryOptions, { items: [] });
              }
            }}
            rowCount={rowCount}
          />
        </Grid>
      </Grid>
    </ReactTableStyles>
  );
}
